import { Flex, Icon, Spinner, TableContainer, Text } from '@chakra-ui/react'
import { HeadingPages } from '../../../components/HeadingPages'
import * as T from '../../../components/Table'
import { api } from '../../../services/api'
import { useQuery } from 'react-query'
import { RelatorioLista } from './RelatorioLista'
import { useEnterprise } from '../../../hooks/useEnterprise'
import { GerarExcel } from './GerarExcel'
import { GerarPdf } from './GerarPdf'
import { useCallback, useMemo, useState } from 'react'
import { abreviacaoEmpresa } from '../../../utils/abreviacaoEmpresa'
import { AReceber } from './AReceber'
import { Summary } from './Summary'
import { Filtros } from './Filtros'
import { FaArrowAltCircleDown } from 'react-icons/fa'

async function getRelatorioDeImposto({ mes, ano, numeroPedido, empresa }) {
  const res = await api.get('/site/v2/fiscal/relatorio-fiscal', {
    params: {
      mes: mes,
      ano: ano,
      numero_pedido: numeroPedido,
      empresa,
    },
  })

  return {
    ...res.data,
    dados_filtrados: res.data.dados_filtrados.map((item) => ({
      ...item,
      nome_empresa: abreviacaoEmpresa(item.nome_empresa),
    })),
  }
}

export function RelatorioDeImposto() {
  const { data: enterprises = [] } = useEnterprise()

  const defaultMonth = new Date().getMonth() + 1
  const defaultMonthString = defaultMonth.toString().padStart(2, '0')
  const defaultYearString = new Date().getFullYear().toString()

  const [filters, setFilters] = useState({
    mes: defaultMonthString,
    ano: defaultYearString,
  })

  const [orderedData, setOrderedData] = useState([])

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc',
  })

  const handleOrder = useCallback(
    (key) => {
      let direction = 'asc'
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc'
      }

      const sortedData = [...orderedData].sort((a, b) => {
        if (direction === 'asc') {
          return a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0
        }
        return a[key] > b[key] ? -1 : a[key] < b[key] ? 1 : 0
      })

      setSortConfig({ key, direction })
      setOrderedData(sortedData)
    },
    [orderedData, sortConfig]
  )

  const {
    data = [],
    isLoading,
    isRefetching,
  } = useQuery(
    ['relatorio-imposto', filters],
    () =>
      getRelatorioDeImposto({
        mes: filters.mes,
        ano: filters.ano,
        numeroPedido: filters.numeroPedido,
        empresa: filters.empresa,
      }),
    {
      onSuccess: (data) => {
        setOrderedData(data?.dados_filtrados)
      },
      //keepPreviousData: true,
      refetchOnWindowFocus: false,
      //staleTime: 1000 * 60 * 5,
    }
  )

  const onSubmit = useCallback((data) => {
    setFilters(data)
  }, [])

  const objectMonth = useMemo(
    () => ({
      1: 'Janeiro',
      2: 'Fevereiro',
      3: 'Março',
      4: 'Abril',
      5: 'Maio',
      6: 'Junho',
      7: 'Julho',
      8: 'Agosto',
      9: 'Setembro',
      10: 'Outubro',
      11: 'Novembro',
      12: 'Dezembro',
    }),
    []
  )

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: currentYear - 2019 + 1 }, (_, i) => (2019 + i).toString())
  }, [])

  return (
    <>
      <HeadingPages name="Relatório de Imposto" />

      <Flex my={10} maxW="1200px" w="full" mx="auto" flexDir="column" justifyContent="center" alignItems="center" gap={2}>
        <Filtros onSubmit={onSubmit} isLoading={isLoading || isRefetching} objectMonth={objectMonth} years={years} enterprises={enterprises} />

        {data?.dados_filtrados?.length > 0 && (
          <Flex alignItems="center" gap={2}>
            <GerarExcel data={orderedData} />
            <GerarPdf data={orderedData} />
          </Flex>
        )}
      </Flex>

      <Flex alignItems="center" justifyContent="space-between" mx={3} mb={2}>
        <Summary data={data} />

        {data?.dados_filtrados && <AReceber data={orderedData} />}
      </Flex>

      {data && (
        <TableContainer>
          <T.Table>
            <T.Thead>
              <T.Th>DATA LIBERAÇÃO</T.Th>
              <T.Th>DATA NFE</T.Th>
              <T.Th>PEDIDO</T.Th>
              <T.Th>EMPRESA</T.Th>
              <T.Th>CLIENTE</T.Th>
              <T.Th>NFE</T.Th>
              <T.Th>VALOR</T.Th>
              <T.Th>PAGAMENTO</T.Th>
              <T.Th>PARCELA</T.Th>
              <T.Th>
                <Flex gap={2} alignItems="center">
                  <Text>VALOR POR PARCELA</Text>
                  <Icon
                    fontSize="18px"
                    cursor="pointer"
                    as={FaArrowAltCircleDown}
                    onClick={() => handleOrder('valor_parcela')}
                    transform={sortConfig.key === 'valor_parcela' && sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)'}
                  />
                </Flex>
              </T.Th>
              <T.Th>VENCIMENTO</T.Th>
              <T.Th>DATA DO PAGAMENTO</T.Th>
              <T.Th>
                <Flex gap={2} alignItems="center">
                  <Text>VALOR RECEBER REAL</Text>
                  <Icon
                    fontSize="18px"
                    cursor="pointer"
                    as={FaArrowAltCircleDown}
                    onClick={() => handleOrder('valor_real')}
                    transform={sortConfig.key === 'valor_real' && sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)'}
                  />
                </Flex>
              </T.Th>
            </T.Thead>
            <T.Tbody>
              {!(isLoading || isRefetching) && <RelatorioLista data={orderedData} />}

              {(isLoading || isRefetching) && (
                <T.Tr>
                  <T.Td colSpan="14" textAlign="center">
                    Carregando... <Spinner size="sm" />
                  </T.Td>
                </T.Tr>
              )}
            </T.Tbody>
          </T.Table>
        </TableContainer>
      )}
    </>
  )
}
